import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { usePlanningPeriod } from "@/context/PlanningPeriodContext";
import { PlanningPeriodSelector } from "@/components/PlanningPeriodSelector";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Download, Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

export default function ImportExport() {
  const { period, query, label } = usePlanningPeriod();
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const exportPlanning = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/export/planning?${query}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `planning_${period.date_from}_${period.date_to}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export gedownload");
    } catch (e) {
      toast.error("Export mislukt");
    } finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/import/volunteers/preview", fd);
      setPreview(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setPreview(null);
    } finally { setBusy(false); }
  };

  const commit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/volunteers/commit", fd);
      toast.success(`${data.created} vrijwilligers geïmporteerd (${data.skipped} overgeslagen)`);
      setPreview(null); setFile(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="import-export-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Import / Export</h1>
        <p className="text-muted-foreground mt-1">Migreer vanuit Excel of exporteer de planning.</p>
      </div>

      <PlanningPeriodSelector />
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-6">
          <div className="w-11 h-11 rounded-lg bg-red-100 dark:bg-red-900/40 flex items-center justify-center mb-3">
            <Download className="w-5 h-5 text-red-600" />
          </div>
          <h3 className="font-heading font-semibold text-lg">Planning exporteren</h3>
          <p className="text-sm text-muted-foreground mt-1 mb-4" data-testid="export-period">Exporteer de planning van {label} naar Excel (met overzicht per vrijwilliger).</p>
          <Button onClick={exportPlanning} disabled={busy} data-testid="export-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />} Exporteer naar Excel
          </Button>
        </Card>

        <Card className="p-6">
          <div className="w-11 h-11 rounded-lg bg-sky-100 dark:bg-sky-900/40 flex items-center justify-center mb-3">
            <Upload className="w-5 h-5 text-sky-600" />
          </div>
          <h3 className="font-heading font-semibold text-lg">Vrijwilligers importeren</h3>
          <p className="text-sm text-muted-foreground mt-1 mb-4">Upload een Excel (.xlsx) of CSV met kolommen zoals Naam, E-mail, Telefoon.</p>
          <input type="file" accept=".xlsx,.csv" onChange={onFile} className="block w-full text-sm text-muted-foreground file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-red-600 file:text-white file:font-medium hover:file:bg-red-700 file:cursor-pointer" data-testid="import-file" />
        </Card>
      </div>

      {preview && (
        <Card className="p-6" data-testid="import-preview">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="font-heading font-semibold">Voorbeeld — deze gegevens worden geïmporteerd</h3>
              <p className="text-sm text-muted-foreground">
                {preview.total} rijen · {preview.valid} geldig · {preview.errors.length} met fouten
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Herkende kolommen — Naam: {preview.mapping.name || "—"}, E-mail: {preview.mapping.email || "—"}, Telefoon: {preview.mapping.phone || "—"}
              </p>
            </div>
            <Button onClick={commit} disabled={busy} data-testid="import-commit">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Definitief importeren
            </Button>
          </div>
          <div className="overflow-x-auto max-h-80">
            <Table>
              <TableHeader>
                <TableRow><TableHead>Naam</TableHead><TableHead>E-mail</TableHead><TableHead>Telefoon</TableHead><TableHead>Status</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {preview.rows.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell>{r.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.email || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.phone || "—"}</TableCell>
                    <TableCell>
                      {r.errors.length ? (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-700"><AlertTriangle className="w-3.5 h-3.5" /> {r.errors.join(", ")}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-red-700"><CheckCircle2 className="w-3.5 h-3.5" /> OK</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
